import { Text } from "@ui-kitten/components";
import { SeatDetails, TicketCartDetails } from "features/types";
import React, { useMemo } from "react";
import { View } from "react-native";
import { formatCurrency } from "utils/appUtils";
import { LINE } from "../helpers";

const ROW_LINE = "#efe7e1";

interface Props {
  seats: SeatDetails[];
  charge: TicketCartDetails | null;
  isLoading: boolean;
  primaryColor: string;
  parkingSpacesCount?: number;
  parkingSectionLabel?: string;
  parkingSelections?: Array<{ section: string; row: string; seat: string }>;
}

const toNumber = (value: string | number | null | undefined) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number") return value;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const SummaryRow = ({
  label,
  value,
  emphasized = false,
  primaryColor,
}: {
  label: string;
  value: string;
  emphasized?: boolean;
  primaryColor?: string;
}) => (
  <View className="flex-row justify-between items-center py-1">
    <Text category={emphasized ? "s1" : "p2"} appearance={emphasized ? undefined : "hint"}>
      {label}
    </Text>
    <Text
      category={emphasized ? "h6" : "s2"}
      style={emphasized && primaryColor ? { color: primaryColor } : undefined}
    >
      {value}
    </Text>
  </View>
);

// Season order summary, V3 look: kicker header, hairline-separated seat
// rows, and a charge breakdown with the total in the tenant accent.
// Mirrors the legacy SeasonPlanOrderSummary's charge math exactly.
const SeasonOrderSummaryV3 = ({
  seats,
  charge,
  isLoading,
  primaryColor,
  parkingSpacesCount,
  parkingSectionLabel,
  parkingSelections,
}: Props) => {
  const seatsByRow = useMemo(() => {
    const grouped: Record<string, SeatDetails[]> = {};
    for (const seat of seats) {
      const sectionKey = seat.sectionDisplay || seat.section;
      const rowKey = seat.rowDisplay || seat.row;
      const key = `${sectionKey}||${rowKey}`;
      (grouped[key] ??= []).push(seat);
    }
    return Object.entries(grouped).sort(([a], [b]) =>
      a.localeCompare(b, undefined, { numeric: true }),
    );
  }, [seats]);

  const subtotal = useMemo(() => {
    if (!charge) return 0;
    return (charge.items ?? []).reduce(
      (sum, item) =>
        sum + toNumber(item.totalCreditCardCharges ?? item.totalPrice),
      0,
    );
  }, [charge]);

  const parkingSubtotal = useMemo(() => {
    if (!charge?.items || !parkingSelections || parkingSelections.length === 0)
      return 0;
    const keys = new Set(
      parkingSelections.map(
        (s) =>
          `${s.section}`.toUpperCase() +
          `~${s.row}`.toUpperCase() +
          `~${s.seat}`.toUpperCase(),
      ),
    );
    return charge.items.reduce((sum, item) => {
      const key =
        `${item.section}`.toUpperCase() +
        `~${item.row}`.toUpperCase() +
        `~${item.seat}`.toUpperCase();
      if (!keys.has(key)) return sum;
      return sum + toNumber(item.totalCreditCardCharges ?? item.totalPrice);
    }, 0);
  }, [charge?.items, parkingSelections]);

  const seatsSubtotal = Math.max(0, subtotal - parkingSubtotal);

  const taxesAndFees = useMemo(() => {
    if (!charge?.feeItems) return 0;
    return charge.feeItems.reduce(
      (sum, item) =>
        sum + toNumber(item.totalCreditCardCharges ?? item.totalPrice),
      0,
    );
  }, [charge]);

  const total = useMemo(() => {
    if (!charge) return subtotal + taxesAndFees;
    const byField = toNumber(charge.totalCreditCardCharges ?? charge.totalPrice);
    return byField || subtotal + taxesAndFees;
  }, [charge, subtotal, taxesAndFees]);

  const hasParking = (parkingSpacesCount ?? 0) > 0;

  return (
    <View testID="v3-season-order-summary">
      <Text
        category="c1"
        appearance="hint"
        style={{ fontWeight: "600", letterSpacing: 1.2 }}
      >
        ORDER SUMMARY
      </Text>

      {/* Selected seats, grouped by section/row with hairline separators. */}
      {seatsByRow.length > 0 ? (
        <View
          className="mt-3"
          style={{
            borderWidth: 1,
            borderColor: LINE,
            borderRadius: 12,
            overflow: "hidden",
          }}
        >
          {seatsByRow.map(([groupKey, rowSeats], index) => {
            const [sectionName, rowName] = groupKey.split("||");
            const seatList = [...rowSeats]
              .sort((a, b) =>
                (a.seatDisplay || a.seat).localeCompare(
                  b.seatDisplay || b.seat,
                  undefined,
                  { numeric: true },
                ),
              )
              .map((s) => s.seatDisplay || s.seat)
              .join(", ");
            return (
              <View
                key={groupKey}
                className="flex-row justify-between items-center px-3 py-2"
                style={{
                  borderTopWidth: index === 0 ? 0 : 1,
                  borderTopColor: ROW_LINE,
                }}
              >
                <Text category="s2">
                  Section {sectionName} · Row {rowName}
                </Text>
                <Text category="c1" appearance="hint">
                  Seat{rowSeats.length > 1 ? "s" : ""} {seatList}
                </Text>
              </View>
            );
          })}
          {parkingSpacesCount !== undefined && hasParking ? (
            <View
              className="flex-row justify-between items-center px-3 py-2"
              style={{ borderTopWidth: 1, borderTopColor: ROW_LINE }}
            >
              <Text category="s2">
                Parking{parkingSectionLabel ? ` · ${parkingSectionLabel}` : ""}
              </Text>
              <Text category="c1" appearance="hint">
                {parkingSpacesCount} space{parkingSpacesCount > 1 ? "s" : ""}
              </Text>
            </View>
          ) : null}
        </View>
      ) : null}
      {parkingSpacesCount !== undefined && hasParking ? (
        <Text category="c1" appearance="hint" style={{ marginTop: 6 }}>
          Parking selection is final. Clear cart to restart.
        </Text>
      ) : null}

      {/* Charges. */}
      <View
        className="mt-3 pt-2"
        style={{ borderTopWidth: 1, borderTopColor: LINE }}
      >
        {isLoading ? (
          <Text appearance="hint" category="c1">
            Calculating…
          </Text>
        ) : null}
        {hasParking && charge ? (
          <>
            <SummaryRow label="Seats" value={`${formatCurrency(seatsSubtotal)}`} />
            <SummaryRow
              label="Parking"
              value={`${formatCurrency(parkingSubtotal)}`}
            />
          </>
        ) : null}
        <SummaryRow
          label="Subtotal"
          value={`${formatCurrency(charge ? subtotal : 0)}`}
        />
        <SummaryRow
          label="Taxes & fees"
          value={`${formatCurrency(charge ? taxesAndFees : 0)}`}
        />
        <View
          style={{ borderTopWidth: 1, borderTopColor: ROW_LINE, marginTop: 4 }}
        >
          <SummaryRow
            label="Total"
            value={`${formatCurrency(charge ? total : 0)}`}
            emphasized
            primaryColor={primaryColor}
          />
        </View>
      </View>
    </View>
  );
};

export default SeasonOrderSummaryV3;
